import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { g, p, beep, TENANT_KEY, STAFF_KEY } from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { ding, speak } from "@/lib/voce";
import { applicaTema, temaDispositivo } from "@/lib/tema";

const Ctx = createContext(null);

export const AppProvider = ({ children }) => {
  const { setLang, t, lang } = useI18n();
  const { has, refreshPlan } = usePlan();
  const [boot, setBoot] = useState(null);
  const [tenant, setTenantState] = useState(null);
  const [tenantLoaded, setTenantLoaded] = useState(false);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rh_user") || "null");
    } catch {
      return null;
    }
  });
  const [sound, setSound] = useState(true);
  const [notifs, setNotifs] = useState([]);
  const seen = useRef(new Set());

  const refresh = useCallback(async () => {
    const b = await g("/bootstrap");
    setBoot(b);
    // se il dispositivo non ha una preferenza, si usa la lingua impostata per il locale
    if (!localStorage.getItem("rh_lang") && b?.settings?.lang) setLang(b.settings.lang);
    // il tema scelto sul dispositivo va riapplicato a ogni avvio, altrimenti si perde
    applicaTema(temaDispositivo() || b?.settings?.theme || "chiaro");
  }, [setLang]);

  const setTenant = useCallback(async (t) => {
    setTenantState(t);
    await refresh();
    await refreshPlan();
  }, [refresh, refreshPlan]);

  useEffect(() => {
    (async () => {
      if (localStorage.getItem(TENANT_KEY)) {
        try {
          const t = await g("/tenant/me");
          if (t) setTenantState(t);
          else localStorage.removeItem(TENANT_KEY);
        } catch {
          localStorage.removeItem(TENANT_KEY);
        }
      }
      setTenantLoaded(true);
      refresh();
    })();
  }, [refresh]);

  const login = (u) => {
    if (u?.session) localStorage.setItem(STAFF_KEY, u.session);
    const { session, ...senzaToken } = u || {};
    localStorage.setItem("rh_user", JSON.stringify(senzaToken));
    setUser(senzaToken);
  };
  const logout = () => {
    p("/staff/logout").catch(() => {});
    localStorage.removeItem(STAFF_KEY);
    localStorage.removeItem("rh_user");
    setUser(null);
  };

  useEffect(() => {
    if (!user) return;
    let stop = false;
    const tick = async () => {
      try {
        const n = await g("/notifications", { to: user.name });
        if (!stop) {
          setNotifs(n);
          n.forEach((x) => {
            if (!seen.current.has(x.id)) {
              seen.current.add(x.id);
              const msg = x.key ? t(x.key, x.params || {}) : x.text;
              // Nella demo gli avvisi del locale finto non si mostrano: confondono chi guarda
              if (boot?.demo_mode) return;
              // L'avviso si legge e va via: sparisce da solo, si chiude col tocco o con la X.
              toast.info(msg, { duration: 8000, closeButton: true, dismissible: true,
                                onClick: (e) => toast.dismiss(e?.id) });
              if (sound) beep(x.kind === "pronto");
              if (sound && has("voice_alerts")) {
                // Voce del dispositivo (italiana): sul server solo se il locale l'ha scelto
                if ((boot?.settings?.voice_engine || "device") === "device") {
                  ding(x.department || "default");
                  speak(msg, x.department || "default", boot?.settings?.voice_device || "");
                } else {
                  p("/tts", { text: msg })
                    .then((r) => new Audio(`${process.env.REACT_APP_BACKEND_URL}${r.url}`).play())
                    .catch(() => speak(msg, x.department || "default", boot?.settings?.voice_device || ""));
                }
              }
            }
          });
        }
      } catch (e) {
        console.warn("Aggiornamento avvisi non riuscito (probabile assenza di rete):", e?.message || e);
      }
    };
    tick();
    const i = setInterval(tick, 12000);
    return () => {
      stop = true;
      clearInterval(i);
    };
  }, [user, sound, has, t, lang, boot?.demo_mode, boot?.settings?.voice_engine, boot?.settings?.voice_device]);

  const clearNotifs = async () => {
    await p("/notifications/read", { ids: notifs.map((n) => n.id) });
    setNotifs([]);
  };

  return (
    <Ctx.Provider
      value={{
        settings: boot?.settings || { business_name: tenant?.name || "Reprehendo" },
        staff: boot?.staff || [],
        zones: boot?.zones || [],
        departments: boot?.departments || ["Cucina", "Pizzeria", "Bar"],
        modules: boot?.modules || null,
        setupDone: !!boot?.setup_done,
        demoMode: !!boot?.demo_mode,
        showcase: !!boot?.showcase,
        viewOnly: !!boot?.view_only,
        support: !!boot?.support,
        vatMissing: !!boot?.vat_missing,
        ownerPinDefault: !!boot?.owner_pin_default,
        refresh, user, login, logout, sound, setSound, notifs, clearNotifs,
        tenant, setTenant, tenantLoaded,
        exitTenant: () => {
          localStorage.removeItem(TENANT_KEY);
          localStorage.removeItem(STAFF_KEY);
          localStorage.removeItem("rh_user");
          // Si esce verso la home: ricaricare /demo/... farebbe rientrare subito nella demo
          window.location.href = "/";
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useApp = () => useContext(Ctx);
